// Today — the patient portal landing screen.
//
// The layout is a vertical stack of contextual widget cards. Each card lives
// behind a stable key (see useTodayCardOrder.js for the canonical list); the
// page renders cards in the per-profile stored order, and the patient can
// reorder via a Customize button at the top.
//
// In customize mode, EVERY card key is rendered — cards with data show their
// real content, cards without data show a small placeholder so the patient
// can rearrange them anyway. Outside customize mode, empty cards return null
// and the page collapses around them.
//
// Data flows from `useToday()` which calls /api/secure/me/today (or its mock
// equivalent). Quick actions (mark medicine taken, log meal, etc.) flow back
// through the hook's mutator helpers.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { TodaySkeleton } from '../../components/Skeleton'
import { usePatient } from '../../context/PatientContext'
import {
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { useToday } from '../../hooks/useToday'
import { useTodayCardOrder } from '../../hooks/useTodayCardOrder'
import { parseUtcDate } from '../../utils/dates'
import {
  clearClinicWalkinBookingHandoff,
  readClinicWalkinBookingHandoff,
} from '../../../shared/clinicWalkinBookingHandoff'

import BlockedSessionsCard from './widgets/BlockedSessionsCard'
import ClinicQueueCard from './widgets/ClinicQueueCard'
import CrisisBannerCard from './widgets/CrisisBannerCard'
import DuesCard from './widgets/DuesCard'
import LiveNowCard from './widgets/LiveNowCard'
import NextSessionCard from './widgets/NextSessionCard'
import MedicinesDueCard from './widgets/MedicinesDueCard'
import MealsTodayCard from './widgets/MealsTodayCard'
import ActivitiesTodayCard from './widgets/ActivitiesTodayCard'
import VitalsToLogCard from './widgets/VitalsToLogCard'
import MoodCheckInCard from './widgets/MoodCheckInCard'
import UnreadMessageCard from './widgets/UnreadMessageCard'
import SessionsTimelineCard from './widgets/SessionsTimelineCard'
import PackProgressCard from './widgets/PackProgressCard'
import GoalNudgeCard from './widgets/GoalNudgeCard'
import ReorderableSlot from './ReorderableSlot'
import WidgetCard from './widgets/WidgetCard'

const HelloRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
`

const Avatar = styled.span`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${(p) => p.$color || '#1F3A5F'};
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.05em;
  flex-shrink: 0;
`

const CustomizeBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 14px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px dashed ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'};
  font-size: 13px;
  flex-wrap: wrap;
`

const ResetLink = styled.button`
  background: transparent;
  border: none;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  &:hover {
    color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

// Customize-mode placeholder: a low-emphasis tile that takes up enough
// height for the ↑/↓ controls to sit on without overlapping their
// neighbours, and tells the patient WHICH card slot they're moving so the
// reorder is meaningful even when the card has no data right now.
const Placeholder = styled.div`
  border: 1px dashed ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'};
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 56px;
  flex-wrap: wrap;
`

const PlaceholderTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
`

const PlaceholderHint = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const HandoffLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const HandoffTitle = styled.h2`
  margin: 0;
  font-size: 26px;
  font-weight: 650;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  line-height: 1.15;
`

const HandoffMeta = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  line-height: 1.45;
`

const HandoffNumber = styled.div`
  min-width: 132px;
  border-left: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  padding-left: 18px;
  @media (max-width: 560px) {
    border-left: none;
    border-top: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
    padding-left: 0;
    padding-top: 14px;
  }
`

const HandoffNumberLabel = styled.div`
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

const HandoffNumberValue = styled.div`
  margin-top: 4px;
  font-size: 36px;
  font-weight: 800;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  line-height: 1;
`

const friendlyTimeOfDay = () => {
  const hour = new Date().getHours()
  if (hour < 5) return 'Late night'
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function formatHandoffWhen(scheduledAt) {
  if (!scheduledAt) return ''
  const d = parseUtcDate(scheduledAt)
  if (!d || Number.isNaN(d.getTime())) return ''
  return d.toLocaleString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function WalkinBookingHandoffCard({ handoff, onDismiss, onViewSession }) {
  const when = formatHandoffWhen(handoff?.scheduledAt)
  const metaParts = [
    handoff?.practitionerName,
    handoff?.clinicName,
    handoff?.patientName ? `Patient: ${handoff.patientName}` : '',
  ].filter(Boolean)

  return (
    <WidgetCard title="Appointment booked" badge={when} tone="success" pulse>
      <HandoffLayout>
        <div>
          <HandoffTitle>{handoff?.title || 'Session'}</HandoffTitle>
          <HandoffMeta>{metaParts.join(' | ') || 'Your appointment is confirmed.'}</HandoffMeta>
        </div>
        {handoff?.appointmentNumber && (
          <HandoffNumber>
            <HandoffNumberLabel>Appointment number</HandoffNumberLabel>
            <HandoffNumberValue>#{handoff.appointmentNumber}</HandoffNumberValue>
          </HandoffNumber>
        )}
      </HandoffLayout>
      <Row $gap="10px" $wrap style={{ marginTop: 18 }}>
        {handoff?.eventId && (
          <PrimaryButton type="button" onClick={onViewSession}>
            View session
          </PrimaryButton>
        )}
        <GhostButton type="button" onClick={onDismiss}>
          Dismiss
        </GhostButton>
      </Row>
    </WidgetCard>
  )
}

// Helper predicates: detect whether a given card's slice has anything
// renderable. Mirror the early-return logic each widget uses internally so
// we know whether to render the real card or a placeholder when in
// customize mode. Stays in TodayPage to keep the registry self-contained.
function hasOfflineSessionTodayNotStarted(s) {
  if (!s || s.sessionMode !== 'OFFLINE' || !s.scheduledAt) return false
  if (s.status && s.status !== 'SCHEDULED') return false
  const scheduledMs = parseUtcDate(s.scheduledAt).getTime()
  const sameDay =
    new Date(scheduledMs).toDateString() === new Date().toDateString()
  if (!sameDay) return false
  // Allow up to 5 min past scheduled — matches ClinicQueueCard's filter.
  const minutesPast = (Date.now() - scheduledMs) / 60000
  return minutesPast <= 5
}

function clinicQueueHasContent(data) {
  if (!data) return false
  if (hasOfflineSessionTodayNotStarted(data.nextSession)) return true
  return Array.isArray(data.upcomingSessions)
    && data.upcomingSessions.some(hasOfflineSessionTodayNotStarted)
}

function blockedSessionsHasContent(data) {
  if (!data) return false
  if (data.nextSession?.paymentBlocked) return true
  return Array.isArray(data.upcomingSessions)
    && data.upcomingSessions.some((s) => s?.paymentBlocked)
}

function liveNowHasContent(data) {
  if (!data) return false
  if (data.nextSession?.canJoinNow) return true
  return Array.isArray(data.upcomingSessions)
    && data.upcomingSessions.some((s) => s?.canJoinNow)
}

function nextSessionHasContent(data) {
  if (!data?.nextSession) return false
  // NextSessionCard internal gating: skipIfJoinable + within 90 min window.
  // We don't perfectly mirror it (the time gating drifts every minute) — for
  // customize-mode placeholder purposes the looser "has a nextSession" check
  // is fine; the card itself still won't render outside its window, but the
  // placeholder won't either since the predicate also guards customize mode.
  if (data.nextSession.canJoinNow) return false // delegated to LiveNow
  if (data.nextSession.paymentBlocked) return false // delegated to BlockedSessions
  return true
}

function sessionsTimelineHasContent(data) {
  if (!data) return false
  const hasPast = Array.isArray(data.pastSessionsToday) && data.pastSessionsToday.length > 0
  const hasUpcoming = Array.isArray(data.upcomingSessions) && data.upcomingSessions.some((s) => {
    if (!s || s.canJoinNow || s.paymentBlocked) return false
    const min = (parseUtcDate(s.scheduledAt).getTime() - Date.now()) / 60000
    return min > 90
  })
  return hasPast || hasUpcoming
}

export default function TodayPage() {
  const navigate = useNavigate()
  const { currentProfile } = usePatient()
  const [walkinHandoff, setWalkinHandoff] = useState(() => readClinicWalkinBookingHandoff())
  const {
    data,
    loading,
    refresh,
    markMedicineTaken,
    completeActivity,
    logVital,
    recordMood,
  } = useToday()
  const {
    order,
    moveUp,
    moveDown,
    isCustomizing,
    toggleCustomize,
    resetOrder,
  } = useTodayCardOrder(currentProfile?.id)

  useEffect(() => {
    setWalkinHandoff(readClinicWalkinBookingHandoff())
  }, [currentProfile?.id])

  if (!currentProfile) return null

  // Greeting: SELF profiles always say "You" (the patient is referring to
  // themselves; their actual name as recorded clinic-side may have multiple
  // spellings across brands and isn't worth resolving on the dashboard).
  // Non-SELF profiles greet with the relative's first word ("Mom",
  // "Anubhav"), stripping any legacy " — Brand" suffix from displayName.
  const rawDisplayName = (currentProfile.displayName || '').trim()
  const namePart = rawDisplayName.includes(' — ')
    ? rawDisplayName.split(' — ')[0].trim()
    : rawDisplayName
  const greetingName =
    currentProfile.relation === 'SELF'
      ? 'You'
      : namePart.split(/\s+/)[0] || 'there'

  // Card registry — single source of truth for keys + labels + content
  // detection + rendering. Adding a new card: add an entry here AND a
  // matching key in DEFAULT_TODAY_CARD_ORDER (useTodayCardOrder.js). The
  // existing-user reconcile path will append the new key at the bottom of
  // their stored order automatically.
  const cardRegistry = {
    clinicQueue: {
      label: 'In-clinic queue',
      hasContent: () => clinicQueueHasContent(data),
      render: () => (
        <ClinicQueueCard
          nextSession={data?.nextSession}
          upcomingSessions={data?.upcomingSessions}
        />
      ),
    },
    crisisBanner: {
      label: 'Crisis banner',
      hasContent: () => Boolean(data?.crisisBanner),
      render: () => <CrisisBannerCard data={data?.crisisBanner} />,
    },
    dues: {
      label: 'Outstanding dues',
      hasContent: () => Boolean(data?.dues),
      render: () => <DuesCard data={data?.dues} />,
    },
    blockedSessions: {
      label: 'Sessions awaiting payment',
      hasContent: () => blockedSessionsHasContent(data),
      render: () => (
        <BlockedSessionsCard
          nextSession={data?.nextSession}
          upcomingSessions={data?.upcomingSessions}
          onPaid={refresh}
        />
      ),
    },
    liveNow: {
      label: 'Live & joinable sessions',
      hasContent: () => liveNowHasContent(data),
      render: () => (
        <LiveNowCard
          nextSession={data?.nextSession}
          upcomingSessions={data?.upcomingSessions}
        />
      ),
    },
    nextSession: {
      label: 'Next session (imminent)',
      hasContent: () => nextSessionHasContent(data),
      render: () => <NextSessionCard data={data?.nextSession} skipIfJoinable />,
    },
    medicines: {
      label: 'Medicines due',
      hasContent: () => Boolean(data?.medicinesDueToday?.length),
      render: () => (
        <MedicinesDueCard
          data={data?.medicinesDueToday}
          onMarkTaken={markMedicineTaken}
        />
      ),
    },
    meals: {
      label: 'Meals',
      hasContent: () => Boolean(data?.mealsToday?.length),
      render: () => <MealsTodayCard data={data?.mealsToday} onLogged={refresh} />,
    },
    activities: {
      label: 'Activities',
      hasContent: () => Boolean(data?.activitiesToday?.length),
      render: () => (
        <ActivitiesTodayCard
          data={data?.activitiesToday}
          onComplete={completeActivity}
        />
      ),
    },
    vitals: {
      label: 'Vitals to log',
      hasContent: () => Boolean(data?.vitalsToLog?.length),
      render: () => <VitalsToLogCard data={data?.vitalsToLog} onLog={logVital} />,
    },
    mood: {
      label: 'Mood check-in',
      hasContent: () => Boolean(data?.moodCheckIn),
      render: () => <MoodCheckInCard data={data?.moodCheckIn} onRecord={recordMood} />,
    },
    unread: {
      label: 'Unread messages',
      hasContent: () => Boolean(data?.unread?.messages),
      render: () => <UnreadMessageCard data={data?.unread} />,
    },
    sessionsTimeline: {
      label: 'Sessions (today + upcoming)',
      hasContent: () => sessionsTimelineHasContent(data),
      render: () => (
        <SessionsTimelineCard
          upcoming={data?.upcomingSessions}
          past={data?.pastSessionsToday}
        />
      ),
    },
    packProgress: {
      label: 'Pack progress',
      hasContent: () => Boolean(data?.packProgress?.length),
      render: () => <PackProgressCard data={data?.packProgress} />,
    },
    goalsNudge: {
      label: 'Goals nudge',
      hasContent: () => Boolean(data?.goalsNudge?.length),
      render: () => <GoalNudgeCard data={data?.goalsNudge} />,
    },
  }

  const hasAnyContent = data
    ? order.some((key) => cardRegistry[key]?.hasContent())
    : false

  // Customize is gated on data existing (we need at least to know the data
  // is loaded before showing the toggle), but doesn't require any card to
  // have content — empty placeholders are still useful for setting a
  // preferred order proactively.
  const showCustomizeToggle = Boolean(data)
  const showWalkinHandoff =
    walkinHandoff &&
    (!walkinHandoff.patientProfileId || walkinHandoff.patientProfileId === currentProfile.id)

  function dismissWalkinHandoff() {
    clearClinicWalkinBookingHandoff()
    setWalkinHandoff(null)
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <HelloRow>
          <Avatar $color={currentProfile.avatarColor}>
            {currentProfile.avatarInitials}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Pill $tone="primary">Today</Pill>
            <PageHeading style={{ marginTop: 8 }}>
              {friendlyTimeOfDay()}, {greetingName}.
            </PageHeading>
            <PageSubheading style={{ marginBottom: 0 }}>
              {currentProfile.brandName ? (
                <>
                  Care at <strong>{currentProfile.brandName}</strong>. Switch profiles from
                  the avatar in the top bar.
                </>
              ) : (
                <>Switch profiles from the avatar in the top bar.</>
              )}
            </PageSubheading>
          </div>
          {showCustomizeToggle ? (
            <GhostButton
              onClick={toggleCustomize}
              style={{ padding: '8px 16px', fontSize: 13 }}
              aria-pressed={isCustomizing}
            >
              {isCustomizing ? 'Done' : 'Customize'}
            </GhostButton>
          ) : null}
        </HelloRow>

        {showWalkinHandoff && (
          <WalkinBookingHandoffCard
            handoff={walkinHandoff}
            onDismiss={dismissWalkinHandoff}
            onViewSession={() => navigate(`/patient-home/sessions/${walkinHandoff.eventId}`)}
          />
        )}

        {isCustomizing && (
          <CustomizeBar role="status">
            <span>
              Use the ↑ / ↓ arrows on each card to reorder. Empty placeholders show every
              available card so you can pre-arrange before data lands. Order saves
              automatically and is kept per profile.
            </span>
            <ResetLink type="button" onClick={resetOrder}>
              Reset to default
            </ResetLink>
          </CustomizeBar>
        )}

        {loading && !data ? (
          <TodaySkeleton />
        ) : !hasAnyContent && !isCustomizing ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>
              Nothing on your plate today.
            </p>
            <Muted>
              When sessions are booked, medicines are prescribed, or activities are
              assigned, they&apos;ll show up right here.
            </Muted>
            <button
              onClick={refresh}
              style={{
                marginTop: 16,
                background: 'transparent',
                border: 'none',
                color: '#1F3A5F',
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Refresh
            </button>
          </div>
        ) : (
          <>
            {order.map((key, i) => {
              const entry = cardRegistry[key]
              if (!entry) return null
              const has = entry.hasContent()
              // Outside customize mode: empty cards are entirely hidden, so
              // their reorder controls (which are absolutely-positioned) can't
              // stack on top of each other. Inside customize mode: render a
              // dashed Placeholder for empty cards so the slot has real
              // height and the ↑/↓ controls anchor to it cleanly.
              if (!has && !isCustomizing) return null
              return (
                <ReorderableSlot
                  key={key}
                  cardKey={key}
                  customizing={isCustomizing}
                  canMoveUp={i > 0}
                  canMoveDown={i < order.length - 1}
                  onMoveUp={moveUp}
                  onMoveDown={moveDown}
                >
                  {has ? (
                    entry.render()
                  ) : (
                    <Placeholder>
                      <PlaceholderTitle>{entry.label}</PlaceholderTitle>
                      <PlaceholderHint>No data right now</PlaceholderHint>
                    </Placeholder>
                  )}
                </ReorderableSlot>
              )
            })}
          </>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
